import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { FileText, Plus, Check, X, Filter, Camera, Sparkles, Loader2, Upload, ScanLine, Zap, Search, DollarSign, TrendingUp, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function Claims() {
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState('all');
  const [activeTab, setActiveTab] = useState('my-claims');
  const [isScanning, setIsScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    claim_type: 'medical',
    amount: '',
    description: '',
    claim_date: new Date().toISOString().split('T')[0]
  });
  
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');
  const isHRAdmin = ['admin', 'hr_manager', 'super_admin', 'platform_admin'].includes(user.role);
  const isManager = ['department_head', 'manager'].includes(user.role);
  const canReview = ['admin', 'hr_manager', 'department_head', 'manager', 'super_admin', 'platform_admin'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [claimsRes, empRes] = await Promise.all([
        axios.get(`${API}/claims`),
        axios.get(`${API}/employees`)
      ]);
      setClaims(claimsRes.data);
      setEmployees(empRes.data);
      
      if (user.role === 'employee' && empRes.data.length > 0) {
        const currentUser = empRes.data.find(e => e.employee_id === user.employee_id);
        if (currentUser) {
          setFormData(prev => ({ ...prev, employee_id: currentUser.employee_id }));
        }
      }
    } catch (error) {
      toast.error('Failed to fetch claims');
    } finally {
      setLoading(false);
    }
  };

  // Claims analytics for HR Admin
  const claimsAnalytics = useMemo(() => {
    const analytics = {
      total: claims.length,
      totalAmount: claims.reduce((sum, c) => sum + (c.amount || 0), 0),
      pending: claims.filter(c => c.status === 'pending'),
      approved: claims.filter(c => c.status === 'approved'),
      rejected: claims.filter(c => c.status === 'rejected'),
      byCategory: {
        medical: { claims: claims.filter(c => c.claim_type === 'medical'), amount: 0 },
        travel: { claims: claims.filter(c => c.claim_type === 'travel'), amount: 0 },
        entertainment: { claims: claims.filter(c => c.claim_type === 'entertainment'), amount: 0 },
        others: { claims: claims.filter(c => c.claim_type === 'others'), amount: 0 }
      }
    };
    
    // Calculate amounts by category
    Object.keys(analytics.byCategory).forEach(cat => {
      analytics.byCategory[cat].amount = analytics.byCategory[cat].claims.reduce((sum, c) => sum + (c.amount || 0), 0);
    });
    
    return analytics;
  }, [claims]);

  // Filter claims for HR Admin view
  const filteredHRClaims = useMemo(() => {
    let filtered = claims;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(c => c.claim_type === selectedCategory);
    }
    
    // Filter by year/month
    filtered = filtered.filter(claim => {
      const claimDate = new Date(claim.claim_date || claim.submitted_date || claim.created_at);
      if (isNaN(claimDate.getTime())) return filterYear === 'all'; // If no valid date, only show when "All Years"
      const year = claimDate.getFullYear().toString();
      const month = (claimDate.getMonth() + 1).toString().padStart(2, '0');
      
      if (filterYear !== 'all' && year !== filterYear) return false;
      if (filterMonth !== 'all' && month !== filterMonth) return false;
      return true;
    });
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(claim => {
        const employee = employees.find(e => e.employee_id === claim.employee_id);
        return (
          employee?.full_name?.toLowerCase().includes(query) ||
          claim.employee_id?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query) ||
          claim.description?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [claims, selectedCategory, filterYear, filterMonth, searchQuery, employees]);

  const handleReceiptScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (JPEG, PNG, etc.)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max 5MB allowed.');
      return;
    }

    setIsScanning(true);
    toast.info('🔍 AI scanning your receipt...');

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        
        try {
          const token = localStorage.getItem('hrms_token');
          const response = await axios.post(
            `${API}/ai/scan-receipt`,
            { image_base64: base64 },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            const data = response.data.data;
            setScannedData(data);
            
            setFormData(prev => ({
              ...prev,
              amount: data.amount?.toString() || prev.amount,
              description: data.description || `${data.vendor_name || ''} - ${data.category || ''}`.trim(),
              claim_type: mapCategory(data.category),
              claim_date: data.date && data.date !== 'unknown' ? data.date : prev.claim_date
            }));

            toast.success(`✨ Receipt scanned! Amount: RM ${data.amount?.toFixed(2) || '0.00'}`);
          }
        } catch (error) {
          console.error('Scan error:', error);
          toast.error('Failed to scan receipt. Please fill manually.');
        } finally {
          setIsScanning(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setIsScanning(false);
      toast.error('Error processing image');
    }
  };

  const mapCategory = (category) => {
    const categoryMap = {
      'medical': 'medical',
      'travel': 'travel',
      'entertainment': 'entertainment',
      'food': 'entertainment',
      'office_supplies': 'others',
      'others': 'others'
    };
    return categoryMap[category?.toLowerCase()] || 'others';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/claims`, {
        ...formData,
        amount: parseFloat(formData.amount),
        employee_id: user.employee_id || formData.employee_id,
        claim_id: `CLM-${Date.now()}`,
        status: 'pending',
        submitted_at: new Date().toISOString()
      });
      toast.success('Claim submitted successfully');
      setDialogOpen(false);
      setScannedData(null);
      setFormData({
        employee_id: user.employee_id || '',
        claim_type: 'medical',
        amount: '',
        description: '',
        claim_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    }
  };

  const handleReview = async (claimId, action) => {
    try {
      await axios.put(`${API}/claims/${claimId}/review?action=${action}`);
      toast.success(`Claim ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} claim`);
    }
  };

  const myClaims = claims.filter(claim => claim.employee_id === user.employee_id);
  const teamClaims = claims.filter(claim => {
    if (!isManager) return false;
    const isTeamClaim = claim.manager_id === user.employee_id || 
      employees.find(e => e.employee_id === claim.employee_id)?.manager_id === user.employee_id;
    return isTeamClaim && claim.employee_id !== user.employee_id;
  });

  const getFilteredClaims = () => {
    let claimsToFilter = claims;
    
    // HR Admins see all claims, Managers see their claims + team claims, Others see only their claims
    if (isHRAdmin) {
      claimsToFilter = claims;
    } else if (isManager) {
      claimsToFilter = activeTab === 'my-claims' ? myClaims : teamClaims;
    } else {
      claimsToFilter = myClaims;
    }
    
    return claimsToFilter.filter(claim => {
      const claimDate = new Date(claim.claim_date || claim.submitted_date || claim.created_at);
      if (isNaN(claimDate.getTime())) return filterYear === 'all';
      const year = claimDate.getFullYear().toString();
      const month = (claimDate.getMonth() + 1).toString().padStart(2, '0');
      
      if (filterYear !== 'all' && year !== filterYear) return false;
      if (filterMonth !== 'all' && month !== filterMonth) return false;
      return true;
    });
  };
  
  const filteredClaims = getFilteredClaims();
  const availableYears = [...new Set(claims.map(c => {
    const d = new Date(c.claim_date || c.submitted_date || c.created_at);
    return isNaN(d.getTime()) ? new Date().getFullYear().toString() : d.getFullYear().toString();
  }))].sort().reverse();

  const getCategoryColor = (category) => {
    const colors = {
      medical: { bg: 'from-blue-500 to-cyan-500', light: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
      travel: { bg: 'from-emerald-500 to-teal-500', light: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
      entertainment: { bg: 'from-purple-500 to-pink-500', light: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
      others: { bg: 'from-slate-500 to-gray-500', light: 'bg-slate-100 dark:bg-slate-900/30', text: 'text-slate-600 dark:text-slate-400' }
    };
    return colors[category] || colors.others;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div></div>;
  }

  // HR Admin Analytics View
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Claims Analytics</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Monitor and review employee expense claims</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="h-5 w-5 text-violet-200" />
              <span className="text-violet-100 text-sm font-medium">Total Claims Value</span>
            </div>
            <p className="text-3xl font-bold">RM {claimsAnalytics.totalAmount.toFixed(2)}</p>
            <p className="text-violet-200 text-sm mt-1">{claimsAnalytics.total} total claims</p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Pending Review</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{claimsAnalytics.pending.length}</p>
            <p className="text-slate-500 text-sm mt-1">
              RM {claimsAnalytics.pending.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2)}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-2">
              <Check className="h-5 w-5 text-emerald-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Approved</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{claimsAnalytics.approved.length}</p>
            <p className="text-slate-500 text-sm mt-1">
              RM {claimsAnalytics.approved.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2)}
            </p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-2 mb-2">
              <X className="h-5 w-5 text-red-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Rejected</span>
            </div>
            <p className="text-3xl font-bold text-red-600">{claimsAnalytics.rejected.length}</p>
            <p className="text-slate-500 text-sm mt-1">
              RM {claimsAnalytics.rejected.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Claims by Category */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Claims by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(claimsAnalytics.byCategory).map(([category, data]) => {
              const colors = getCategoryColor(category);
              const isSelected = selectedCategory === category;
              
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(isSelected ? 'all' : category)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected 
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors.bg} flex items-center justify-center mb-3`}>
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 capitalize">{category}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{data.claims.length}</p>
                  <p className="text-sm text-slate-500 mt-1">RM {data.amount.toFixed(2)}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by name, employee ID, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
                style={{ borderRadius: '12px' }}
              />
            </div>
            <select 
              value={filterYear} 
              onChange={(e) => setFilterYear(e.target.value)} 
              className="h-10 px-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100" 
              style={{ borderRadius: '12px' }}
            >
              <option value="all">All Years</option>
              {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
            <select 
              value={filterMonth} 
              onChange={(e) => setFilterMonth(e.target.value)} 
              className="h-10 px-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100" 
              style={{ borderRadius: '12px' }}
            >
              <option value="all">All Months</option>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                <option key={m} value={(i + 1).toString().padStart(2, '0')}>{m}</option>
              ))}
            </select>
            {selectedCategory !== 'all' && (
              <button
                onClick={() => setSelectedCategory('all')}
                className="text-sm text-violet-600 hover:text-violet-700"
              >
                Clear category filter
              </button>
            )}
          </div>
        </div>

        {/* Claims Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {selectedCategory !== 'all' ? `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Claims` : 'All Claims'}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">{filteredHRClaims.length} claims found</p>
            </div>
            <span className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-3 py-1 rounded-full text-sm font-medium">
              {claims.filter(c => c.status === 'pending').length} pending review
            </span>
          </div>

          {filteredHRClaims.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                {searchQuery ? 'No claims found matching your search' : 'No claims found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Category</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Amount</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Date</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Description</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Status</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {filteredHRClaims.map(claim => {
                    const employee = employees.find(e => e.employee_id === claim.employee_id);
                    const colors = getCategoryColor(claim.claim_type);
                    
                    return (
                      <tr key={claim.claim_id} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {(claim.employee_name || employee?.full_name || 'U').charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-slate-100">{claim.employee_name || employee?.full_name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{claim.department || employee?.department || ''}</p>
                              <p className="text-xs text-slate-400">{employee?.email || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${colors.light} ${colors.text}`}>
                            {claim.claim_type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            RM {claim.amount?.toFixed(2) || '0.00'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                          {new Date(claim.claim_date || claim.submitted_date || claim.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                          {claim.description || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                            claim.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                            claim.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {claim.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {claim.status === 'pending' ? (
                            <div className="flex items-center justify-center space-x-2">
                              <Button 
                                size="sm" 
                                onClick={() => handleReview(claim.claim_id, 'approved')} 
                                className="bg-emerald-600 hover:bg-emerald-700 h-8" 
                                style={{ borderRadius: '8px' }}
                              >
                                <Check className="h-3 w-3 mr-1" />Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleReview(claim.claim_id, 'rejected')} 
                                className="h-8" 
                                style={{ borderRadius: '8px' }}
                              >
                                <X className="h-3 w-3 mr-1" />Reject
                              </Button>
                            </div>
                          ) : (
                            <span className="text-sm text-slate-500 dark:text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Employee/Manager View (Original)
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Claims & Reimbursements</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {isManager && activeTab === 'team-claims' 
              ? 'Review and approve team expense claims' 
              : 'Submit and track your expense claims'}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700" style={{ borderRadius: '12px' }}>
          <Sparkles className="h-4 w-4 mr-2" />
          AI-Powered Claim
        </Button>
      </div>

      {/* Tabs for Managers */}
      {isManager && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1.5" style={{ borderRadius: '16px' }}>
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('my-claims')}
              className={`flex-1 px-6 py-3 text-sm font-semibold transition-all ${
                activeTab === 'my-claims'
                  ? 'bg-slate-900 dark:bg-emerald-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              style={{ borderRadius: '12px' }}
            >
              My Own Claims
              {myClaims.length > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs ${
                  activeTab === 'my-claims' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`} style={{ borderRadius: '8px' }}>
                  {myClaims.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('team-claims')}
              className={`flex-1 px-6 py-3 text-sm font-semibold transition-all ${
                activeTab === 'team-claims'
                  ? 'bg-slate-900 dark:bg-emerald-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              style={{ borderRadius: '12px' }}
            >
              My Team Claims
              {teamClaims.length > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs ${
                  activeTab === 'team-claims' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`} style={{ borderRadius: '8px' }}>
                  {teamClaims.length}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4" style={{ borderRadius: '16px' }}>
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="h-4 w-4 text-slate-600 dark:text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Filter Claims</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Year</label>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" style={{ borderRadius: '12px' }}>
              <option value="all">All Years</option>
              {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Month</label>
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" style={{ borderRadius: '12px' }}>
              <option value="all">All Months</option>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                <option key={m} value={(i + 1).toString().padStart(2, '0')}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Claims Table */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden" style={{ borderRadius: '16px' }}>
        {filteredClaims.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No claims found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Employee</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Type</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Amount</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Date</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Status</th>
                {canReview && <th className="text-center px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Action</th>}
              </tr>
            </thead>
            <tbody>
              {filteredClaims.map(claim => (
                <tr 
                  key={claim.claim_id} 
                  className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                  onClick={() => {
                    const employeeName = claim.employee_name || employees.find(e => e.employee_id === claim.employee_id)?.full_name || 'Unknown';
                    toast.info(`Claim Details\n\nEmployee: ${employeeName}\nType: ${claim.claim_type}\nAmount: RM ${claim.amount.toFixed(2)}\nDate: ${new Date(claim.claim_date || claim.submitted_date || claim.created_at).toLocaleDateString('en-MY')}\nStatus: ${claim.status}\nDescription: ${claim.description || 'N/A'}`);
                  }}
                  title="Click to view details"
                >
                  <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">{claim.employee_name || employees.find(e => e.employee_id === claim.employee_id)?.full_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 capitalize">{claim.claim_type}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">RM {claim.amount.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">{new Date(claim.claim_date || claim.submitted_date || claim.created_at).toLocaleDateString('en-MY')}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium capitalize ${
                      claim.status === 'approved' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' :
                      claim.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`} style={{ borderRadius: '8px' }}>{claim.status}</span>
                  </td>
                  {canReview && (
                    <td className="px-6 py-4 text-center">
                      {claim.status === 'pending' && (
                        <>
                          {isManager && activeTab === 'team-claims' && claim.employee_id !== user.employee_id && (
                            <div className="flex items-center justify-center space-x-2">
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleReview(claim.claim_id, 'approved'); }} className="bg-emerald-600 hover:bg-emerald-700 h-8" style={{ borderRadius: '8px' }}>
                                <Check className="h-3 w-3 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleReview(claim.claim_id, 'rejected'); }} className="h-8" style={{ borderRadius: '8px' }}>
                                <X className="h-3 w-3 mr-1" />Reject
                              </Button>
                            </div>
                          )}
                          {!isManager && (
                            <div className="flex items-center justify-center space-x-2">
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleReview(claim.claim_id, 'approved'); }} className="bg-emerald-600 hover:bg-emerald-700 h-8" style={{ borderRadius: '8px' }}>
                                <Check className="h-3 w-3 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleReview(claim.claim_id, 'rejected'); }} className="h-8" style={{ borderRadius: '8px' }}>
                                <X className="h-3 w-3 mr-1" />Reject
                              </Button>
                            </div>
                          )}
                          {isManager && activeTab === 'my-claims' && (
                            <span className="text-sm text-slate-500 dark:text-slate-400">Awaiting approval</span>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Submit Claim Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" style={{ borderRadius: '24px' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Sparkles className="h-5 w-5 mr-2 text-violet-500" />
              AI-Powered Claim Submission
            </DialogTitle>
          </DialogHeader>
          
          {/* AI Receipt Scanner */}
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 p-4 border border-violet-200 dark:border-violet-800" style={{ borderRadius: '16px' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <ScanLine className="h-5 w-5 text-violet-600" />
                <span className="font-semibold text-violet-900 dark:text-violet-100">AI Receipt Scanner</span>
              </div>
              <span className="text-xs bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 px-2 py-1" style={{ borderRadius: '6px' }}>
                <Zap className="h-3 w-3 inline mr-1" />OCR Powered
              </span>
            </div>
            <p className="text-sm text-violet-700 dark:text-violet-300 mb-3">
              Upload a receipt photo and let AI extract the details automatically!
            </p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleReceiptScan}
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isScanning}
              className="w-full border-violet-300 dark:border-violet-600 hover:bg-violet-100 dark:hover:bg-violet-800"
              style={{ borderRadius: '12px' }}
            >
              {isScanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning Receipt...
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  Upload Receipt Photo
                </>
              )}
            </Button>
            
            {scannedData && (
              <div className="mt-3 p-3 bg-white dark:bg-slate-800 text-sm" style={{ borderRadius: '12px' }}>
                <p className="text-emerald-600 dark:text-emerald-400 font-medium flex items-center">
                  <Check className="h-4 w-4 mr-1" />
                  Receipt scanned successfully!
                </p>
                <p className="text-slate-600 dark:text-slate-400 mt-1">
                  Vendor: {scannedData.vendor_name} • Confidence: {scannedData.confidence}
                </p>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Claim Type</Label>
              <select 
                value={formData.claim_type} 
                onChange={(e) => setFormData({ ...formData, claim_type: e.target.value })} 
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                style={{ borderRadius: '12px' }}
              >
                <option value="medical">Medical</option>
                <option value="travel">Travel</option>
                <option value="entertainment">Entertainment</option>
                <option value="others">Others</option>
              </select>
            </div>
            <div>
              <Label>Amount (RM)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={formData.amount} 
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })} 
                required 
                placeholder="0.00"
                style={{ borderRadius: '12px' }}
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input 
                type="date" 
                value={formData.claim_date} 
                onChange={(e) => setFormData({ ...formData, claim_date: e.target.value })} 
                required 
                style={{ borderRadius: '12px' }}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                required 
                placeholder="Brief description of expense"
                style={{ borderRadius: '12px' }}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setScannedData(null); }} style={{ borderRadius: '12px' }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700" style={{ borderRadius: '12px' }}>
                <Sparkles className="h-4 w-4 mr-2" />
                Submit Claim
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
